'use memo';

import { useState } from 'react';

import ActionButton from '@/components/Button/ActionButton';
import Icon from '@/components/Icon/Icon';
import { Text } from '@/components/Typography';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type {
  AiChatModel,
  ChatAgent,
} from '@/features/global-ai-chat/api/types';
import { cn } from '@/lib/cn';

type AiChatModelSelectorProps = {
  models: AiChatModel[];
  selectedModelId: ChatAgent | null;
  onSelectModel: (modelId: ChatAgent) => void;
};

export function AiChatModelSelector({
  models,
  selectedModelId,
  onSelectModel,
}: AiChatModelSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);

  const selectedModel =
    models.find((model) => model.id === selectedModelId) ?? models[0];

  if (!selectedModel) {
    return null;
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <ActionButton
          type="button"
          size="sm"
          variant="mutedGhost"
          icon="ChevronDownIcon"
          iconPosition="right"
        >
          {selectedModel.label}
        </ActionButton>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        // PopoverContent デフォルトは z-50。AiChatPanel が z-60 のため裏に隠れる。
        // 履歴 Popover と同様 z-70 でパネルの上に出す。
        className="z-70 w-72 border-neutral-muted-border bg-neutral-fill p-1"
      >
        <div className="flex flex-col gap-1">
          {models.map((model) => {
            const isSelected = model.id === selectedModel.id;
            return (
              <button
                key={model.id}
                type="button"
                className={cn(
                  'flex items-start gap-2 rounded-md px-3 py-2 text-left hover:bg-primary-muted-fill',
                  isSelected && 'bg-primary-muted-fill',
                )}
                onClick={() => {
                  onSelectModel(model.id);
                  setIsOpen(false);
                }}
              >
                <Icon
                  iconName="Check"
                  size={16}
                  className={cn(
                    'mt-0.5 shrink-0 text-primary-on-neutral-fill',
                    !isSelected && 'invisible',
                  )}
                />
                <div className="flex flex-col gap-0.5">
                  <Text variant="labelSmall" className="text-neutral-on-fill">
                    {model.label}
                  </Text>
                  <Text
                    variant="labelSmallReg"
                    className="text-neutral-muted-on-fill"
                  >
                    {model.description}
                  </Text>
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
